import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Crown, Users, Calendar, FileText, Zap, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useFamilySubscription } from '@/hooks/useFamilySubscription';
import { useToast } from '@/hooks/use-toast';

interface SubscriptionSuccessProps {
  onContinue?: () => void;
}

const features = [
  {
    icon: Users,
    title: "Invitations illimitées",
    description: "Invitez autant de membres que vous voulez dans votre famille"
  },
  {
    icon: Calendar,
    title: "Événements avancés",
    description: "Créez des événements récurrents et partagez-les publiquement"
  },
  {
    icon: FileText,
    title: "Documents illimités",
    description: "Stockez tous vos documents familiaux importants"
  },
  {
    icon: Zap,
    title: "Fonctionnalités premium",
    description: "Accédez à toutes les fonctionnalités avancées"
  }
];

export function SubscriptionSuccess({ onContinue }: SubscriptionSuccessProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const familyId = searchParams.get('family_id') || undefined;
  const { refreshSubscriptionStatus, subscribed, isTrialing } = useFamilySubscription(familyId);
  const { toast } = useToast();
  const [isPolling, setIsPolling] = useState(true);
  const [pollAttempts, setPollAttempts] = useState(0);

  useEffect(() => {
    let pollInterval: NodeJS.Timeout;
    
    const startPolling = async () => {
      // Force refresh subscription status when component mounts
      refreshSubscriptionStatus();
      
      // Show success toast
      toast({
        title: "🎉 Paiement réussi !",
        description: "Finalisation de votre abonnement en cours...",
        duration: 3000,
      });

      // Poll every 3 seconds until subscription is detected or timeout
      pollInterval = setInterval(async () => {
        setPollAttempts(prev => prev + 1);
        
        if (pollAttempts >= 10) { // Max 30 seconds
          setIsPolling(false);
          clearInterval(pollInterval);
          toast({
            title: "⚠️ Finalisation en cours",
            description: "Votre abonnement sera bientôt actif. Vous pouvez continuer.",
            duration: 5000,
          });
          return;
        }

        refreshSubscriptionStatus();
      }, 3000);
    };

    startPolling();

    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [refreshSubscriptionStatus, toast]);

  // Stop polling when subscription is detected
  useEffect(() => {
    if ((subscribed || isTrialing) && isPolling) {
      setIsPolling(false);
      toast({
        title: "✅ Abonnement activé !",
        description: "Votre abonnement Clan Pro est maintenant actif.",
        duration: 5000,
      });
    }
  }, [subscribed, isTrialing, isPolling, toast]);

  const handleContinue = () => {
    if (onContinue) {
      onContinue();
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            {isPolling ? (
              <Loader2 className="h-8 w-8 text-primary animate-spin" />
            ) : (
              <Crown className="h-8 w-8 text-primary" />
            )}
          </div>
          <CardTitle className="text-2xl font-bold text-primary">
            {isPolling ? "Finalisation en cours..." : "Bienvenue dans Clan Pro !"}
          </CardTitle>
          <p className="text-muted-foreground">
            {isPolling 
              ? "Activation de votre abonnement..." 
              : "Votre abonnement a été activé avec succès"
            }
          </p>
        </CardHeader>

        <CardContent className="space-y-4">
          {!isPolling && (
            <div className="space-y-3">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                    <feature.icon className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-medium text-sm">{feature.title}</h4>
                    <p className="text-xs text-muted-foreground">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {isPolling && (
            <div className="text-center py-8">
              <p className="text-sm text-muted-foreground">
                Synchronisation avec Stripe en cours...
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Tentative {pollAttempts}/10
              </p>
            </div>
          )}

          <Button 
            onClick={handleContinue}
            className="w-full mt-6"
            size="lg"
            disabled={isPolling}
          >
            {isPolling ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Finalisation...
              </>
            ) : (
              <>
                <Zap className="h-4 w-4 mr-2" />
                Commencer à utiliser Clan Pro
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}